import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import {
  FormatQuote as QuoteIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

const FeedbackCard = ({ feedback }) => {
  const [detailOpen, setDetailOpen] = useState(false);

  const initials = feedback.studentName
    ? feedback.studentName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  return (
    <>
      <Card
        onClick={() => setDetailOpen(true)}
        sx={{
          borderRadius: 3,
          background: "white",
          border: "1px solid rgba(0,0,0,0.08)",
          transition: "all 0.3s ease",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          cursor: "pointer",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(33, 150, 243, 0.12)",
            transform: "translateY(-2px)",
          },
        }}
      >
        <CardContent sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Quote icon */}
          <QuoteIcon
            sx={{
              fontSize: 32,
              color: "rgba(33, 150, 243, 0.2)",
              mb: 1,
            }}
          />

          {/* Feedback text */}
          <Typography
            variant="body2"
            sx={{
              color: "#444",
              lineHeight: 1.7,
              mb: 2,
              flex: 1,
              fontStyle: "italic",
              display: "-webkit-box",
              WebkitLineClamp: 4,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            "{feedback.feedbackText}"
          </Typography>

          {/* Score image */}
          {feedback.scoreImage && (
            <Box
              sx={{
                mb: 2,
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              <Box
                component="img"
                src={feedback.scoreImage}
                alt={`${feedback.studentName}'s score`}
                sx={{
                  width: "100%",
                  height: 120,
                  objectFit: "cover",
                }}
              />
            </Box>
          )}

          {/* Student info */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: "auto" }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background:
                  "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
                fontSize: "0.85rem",
                fontWeight: 600,
              }}
            >
              {initials}
            </Avatar>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#333" }}
            >
              {feedback.studentName}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        {/* Close button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1, pb: 0 }}>
          <IconButton onClick={() => setDetailOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 3, pb: 3, pt: 0 }}>
          {/* Quote icon */}
          <QuoteIcon
            sx={{
              fontSize: 40,
              color: "rgba(33, 150, 243, 0.2)",
              mb: 1,
            }}
          />

          {/* Full feedback text */}
          <Typography
            variant="body1"
            sx={{
              color: "#444",
              lineHeight: 1.8,
              mb: 3,
              fontStyle: "italic",
              fontSize: "1rem",
            }}
          >
            "{feedback.feedbackText}"
          </Typography>

          {/* Full score image */}
          {feedback.scoreImage && (
            <Box
              sx={{
                mb: 3,
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              <Box
                component="img"
                src={feedback.scoreImage}
                alt={`${feedback.studentName}'s score`}
                sx={{
                  width: "100%",
                  display: "block",
                  borderRadius: 2,
                }}
              />
            </Box>
          )}

          {/* Student info */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 42,
                height: 42,
                background:
                  "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
                fontSize: "0.95rem",
                fontWeight: 600,
              }}
            >
              {initials}
            </Avatar>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "#333" }}
            >
              {feedback.studentName}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackCard;
